<!-- 
Listing of levy collection providers.
-->
<template>
  <div>
    <v-card :class="embedded ? 'v-card--flat' : 'home'">
      <our-table-header 
        :listing="listing"
        :search-filters="searchFilters"
        :criteria-mutation="criteriaMutation"
        :has-export="hasExport"
        @update:criteria="refresh"
        @update:pageNumber="updatePageNumber" 
        @click:refresh="refresh" 
        @click:export="exportToExcel" 
      />
      <our-table-busy-indicator :show="listing.loading"/>
      <v-data-table 
        ref="levyCollectionTable" 
        :headers="headers" 
        :items="listing.data" 
        :pagination="listing.paging" 
        :total-items="listing.paging.totalItems" 
        :rows-per-page-items="rowsPerPageItems" 
        :no-data-text="noDataText"
        must-sort 
        hide-actions
        @update:pagination="updatePagination"
      >
        <template slot="items" slot-scope="props">
          <tr @click.stop="rowSelected(props.item.providerId, props.item)">
            <our-nav-col :row="`${props.item.id}`"/>
            <td class="pa-0 ma-0 text-xs-center">
              <our-warnings-tooltip :warnings="props.item.warnings" />
            </td>
            <td>{{ props.item.providerName }}</td>
            <td>{{ props.item.providerCode }}</td>
            <td>
              <our-favourite-button :lcp="props.item" />
            </td>
            <td>{{ lookups.levyCollectionProviderStatusDescriptions[props.item.status] }}</td>
            <td v-if="anyNotes" class="pa-0 ma-0 text-xs-center">
              <v-tooltip v-if="props.item.hasNotes" bottom>
                <v-btn 
                  slot="activator" 
                  :to="`/levy-collections/${levyCollectionId}/providers/${props.item.providerId}/notes`" 
                  :color="props.item.hasReminders || props.item.hasNotesForLevyCollection ? 'warning darken-1' : 'grey darken-2'"
                  flat class="notes-icon"
                  @click.stop
                >
                  <v-icon>
                    event_note
                  </v-icon>
                </v-btn>
                <span>Provider has notes{{ !props.item.hasNotesForLevyCollection && !props.item.hasReminders ? "." : "" }}</span>
                <span>
                  {{ props.item.hasReminders ? "and/or reminders" : "" }}
                  {{ props.item.hasNotesForLevyCollection || props.item.hasReminders ? "for this levy collection." : "" }}
                  Click to view them.
                </span>
              </v-tooltip>
            </td>
            <td class="hidden-md-and-down">
              <p v-for="warning in props.item.warnings" :key="warning.type" class="my-0">
                {{ lookups.levyCollectionProviderWarningTypeDescriptions[warning.type] }}{{ (warning.description ? ":" : ".") }} {{ warning.description | fullStop }}
              </p>
            </td>
            <td class="text-xs-right">{{ props.item.totalLevy | currency }}</td>
            <td class="text-xs-center px-2">
              <v-tooltip bottom>
                <v-btn 
                  slot="activator"
                  :to="`/levy-collections/${levyCollectionId}/providers/${props.item.providerId}`" icon small target="_blank" 
                  class="ma-0 primary--text" @click.stop
                >
                  <v-icon small>open_in_new</v-icon>
                </v-btn>
                <span>Open this record in a new tab</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <our-table-footer :paging="listing.paging" @update:pageNumber="updatePageNumber" @update:rowsPerPage="updateRowsPerPage" />
    </v-card>
  </div>
</template>

<script>
import _ from "underscore";
import tableMixins from "@/mixins/table.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as action from "@/store/levy-collection-providers/action-types";
import * as mutation from "@/store/levy-collection-providers/mutation-types";
import { LEVY_COLLECTION_PROVIDERS_EXPORT } from "@/store/claim-types";
import { getSearchFilters } from "@/store/levy-collection-providers/helpers";

export default {
  mixins: [tableMixins, lookupMixins, filterMixins],
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      action: action.LIST,
      exportAction: action.EXPORT,
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      clearCriteriaMutation: mutation.LIST_CLEAR_CRITERIA,
      pagingMutation: mutation.LIST_SET_PAGING
    };
  },
  computed: {
    headers() {
      const h = [];
      h.push({ text: "", value: "Id", class: "navCol", width: 0, sortable: false });
      h.push({ text: "", value: "Warnings", sortable: false, width: 10 });
      h.push({ text: "Legal name", value: "Provider.Name", sortable: this.canSort });
      h.push({ text: "Provider code", value: "Provider.ProviderCode", sortable: this.canSort, width: 10 });
      h.push({ text: "Favourite", value: "favourite", sortable: this.canSort, width: 10 });
      h.push({ text: "Status", value: "Status", sortable: this.canSort });
      if (this.anyNotes) {
        h.push({ text: "", value: "Note", sortable: false, width: 10 });
      }
      h.push({ text: "Warnings & flags", value: "Notes", sortable: false, class: "hidden-md-and-down" });
      h.push({
        text: "Total levy",
        value: "LevyCalculationTotalLevy",
        align: "right",
        sortable: this.canSort
      });
      h.push({ text: "", value: "Id", sortable: false, align: "right", width: 100 });
      return h;
    },
    state() {
      return this.$store.state.levyCollectionProviders;
    },
    listing() {
      return this.state.listing;
    },
    loaded() {
      return this.listing.loaded;
    },
    favourite() {
      return this.state.favourite;
    },
    taskProcessing() {
      return this.state.task.processing;
    },
    hasExport() {
      return this.$store.getters.claims.includes(LEVY_COLLECTION_PROVIDERS_EXPORT);
    },
    anyNotes() {
      return _(this.listing.data).some(p => p.hasNotes);
    },
    searchFilters() {
      return getSearchFilters();
    },
    criteria() {
      return this.listing.criteria;
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      const needsReloaded =
        (!this.listing.loaded && !this.listing.loading) || this.state.listing.levyCollectionId != this.levyCollectionId;
      if (needsReloaded) {
        this.$store.commit(mutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId });
        this.populate();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.notes-icon {
  margin: 0;
  padding: 0px;
  min-width: 35px;
  height: 30px;
  i {
    font-size: 22px;
  }
}
</style>